import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import BreakdownPanel from '../bie/BreakdownPanel';
import MarginBridge from '../bie/MarginBridge';
import { BieMoney } from '../bie/BieValue';

type FinancialMode = 'MANAGEMENT' | 'SYSCOHADA';

type SyscohadaLine = {
  lineid?: number;
  linecode?: string;
  linename?: string;
  isderived?: boolean;
  sortorder?: number;
  basevalue?: number;
  resultvalue?: number;
  expressions?: string;
};

type Props = { data?: any };
type State = { mode: FinancialMode };

class SimulationFinancialAnalysisPanel extends React.PureComponent<Props, State> {
  state: State = { mode: 'MANAGEMENT' };

  private setMode = (_: React.MouseEvent<HTMLElement>, value: FinancialMode | null) => {
    if (value) this.setState({ mode: value });
  };

  private getSyscohadaLines = (): SyscohadaLine[] => {
    const financials = this.props.data || {};
    const payload = financials.syscohada || {};
    const lines = Array.isArray(payload) ? payload : payload.lines;
    if (!Array.isArray(lines)) return [];

    return [...lines].sort((a, b) => {
      const ao = Number(a?.sortorder ?? 999999);
      const bo = Number(b?.sortorder ?? 999999);
      if (ao !== bo) return ao - bo;
      return String(a?.linecode || '').localeCompare(String(b?.linecode || ''));
    });
  };

  private renderManagementView() {
    const d = this.props.data || {};

    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <BreakdownPanel title="Revenue Breakdown" rows={d.revenue || []} />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <BreakdownPanel title="Cost & Inventory Analysis" rows={d.costs || []} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <MarginBridge rows={d.bridge || []} />
        </Grid>
      </Grid>
    );
  }

  private renderSyscohadaView() {
    const lines = this.getSyscohadaLines();

    if (!lines.length) {
      return (
        <Alert severity="info">
          No Simulation SYSCOHADA statement is available for this scenario yet.
        </Alert>
      );
    }

    return (
      <Stack spacing={2}>
        <Alert severity="info" icon={<AccountBalanceIcon />}>
          SYSCOHADA presentation uses the same current Simulation results as the Management View,
          while preserving the governed SYSCOHADA line and formula structure. Derived lines are
          highlighted and calculated from the Simulation-specific formula registry.
        </Alert>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Simulation SYSCOHADA statement">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, width: 80 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>SYSCOHADA Line</TableCell>
                <TableCell sx={{ fontWeight: 800, width: 130 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Basis / Formula</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, width: 190 }}>
                  Result
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {lines.map((line, index) => {
                const derived = Boolean(line.isderived);
                const formula = String(line.expressions || '').trim();
                const result = Number(line.resultvalue ?? line.basevalue ?? 0);

                return (
                  <TableRow
                    key={`${line.linecode || 'LINE'}-${line.lineid ?? index}`}
                    sx={derived ? { bgcolor: 'action.hover' } : undefined}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: derived ? 900 : 700 }}
                      >
                        {line.linecode || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: derived ? 900 : 500 }}
                      >
                        {line.linename || line.linecode || 'SYSCOHADA line'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={derived ? 'primary' : 'default'}
                        label={derived ? 'Derived' : 'Mapped'}
                      />
                    </TableCell>

                    <TableCell>
                      {derived && formula ? (
                        <Typography
                          variant="caption"
                          component="code"
                          sx={{ fontWeight: 700, whiteSpace: 'normal' }}
                        >
                          {formula}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Simulation mapping
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: derived ? 900 : 700, whiteSpace: 'nowrap' }}
                      >
                        <BieMoney value={result} />
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="space-between"
          >
            <Typography variant="caption" color="text.secondary">
              Mapped lines are sourced from authoritative Simulation outputs. Derived lines are
              evaluated using the Simulation SYSCOHADA formula registry.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {lines.length} statement lines
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  render() {
    const { mode } = this.state;

    return (
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Financial Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review the same computed scenario through management or SYSCOHADA presentation.
              </Typography>
            </Box>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={this.setMode}
              aria-label="Financial presentation mode"
            >
              <ToggleButton value="MANAGEMENT" aria-label="Management View">
                <AnalyticsIcon fontSize="small" sx={{ mr: 0.75 }} />
                Management View
              </ToggleButton>
              <ToggleButton value="SYSCOHADA" aria-label="SYSCOHADA View">
                <AccountBalanceIcon fontSize="small" sx={{ mr: 0.75 }} />
                SYSCOHADA View
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Paper>

        {mode === 'SYSCOHADA'
          ? this.renderSyscohadaView()
          : this.renderManagementView()}
      </Stack>
    );
  }
}

export default SimulationFinancialAnalysisPanel;
